import React, { useEffect, useCallback, useState } from 'react'
import { withI18n } from '@active/react-utils/i18n'
import {
  func, string,
} from 'prop-types'
import noop from 'lodash/noop'
import {
  VerifyPhoneStepOne,
  VerifyPhoneStepTwo,
  mfaConfig,
} from '../../../common/MFAByStep'
import { calculateCounter } from '../../../common/MFAContent/MFAContent'
import { messageUtils } from '../../../../shared/utils/commonUtils'

function PhoneAuthenticationByStep({
  onSetUpPhone,
  configureTrack,
  epid,
  stepMode,
  locale,
  senderPhone,
  nextTimeWindow,
}) {
  const [countDownNumber, setCountDownNumber] = useState(calculateCounter(nextTimeWindow))

  useEffect(() => {
    const interval = setInterval(() => {
      const count = calculateCounter(nextTimeWindow)
      if (count <= 0) clearInterval(interval)
      setCountDownNumber(calculateCounter(nextTimeWindow))
    }, 1000)
    return () => {
      clearInterval(interval)
    }
  }, [nextTimeWindow])

  const handleEventTracking = useCallback(event => {
    configureTrack({
      version: 'v5',
      pageType: 'sign in|phone setup',
      mfaChannel: 'SMS',
      widget: 'signIn_phone_setup',
      eventTracking: event,
      epid: epid,
    })
  }, [configureTrack, epid])

  useEffect(() => {
    if (stepMode === mfaConfig.StepModeMap.SETUP_PHONE_STEP_ONE) {
      handleEventTracking('init')
    }
  }, [handleEventTracking, stepMode])

  const handleOperation = useCallback(async params => {
    const { message } = await onSetUpPhone(params)
    handleEventTracking(params.operation)
    message && messageUtils.alertError(message)
  }, [onSetUpPhone, handleEventTracking])

  const handleSkip = useCallback(() => {
    handleOperation({
      operation: mfaConfig.SetPhoneOperationBackEndMap.PHONE_SETUP_SKIP,
    })
  }, [handleOperation])

  const handleSend = useCallback(values => {
    handleOperation({
      operation: mfaConfig.SetPhoneOperationBackEndMap.PHONE_SETUP_SEND,
      ...values,
    })
  }, [handleOperation])

  const handleBack = useCallback(() => {
    handleOperation({
      operation: mfaConfig.SetPhoneOperationBackEndMap.PHONE_SETUP_BACK,
    })
  }, [handleOperation])

  const handleResend = useCallback(() => {
    handleOperation({
      operation: mfaConfig.SetPhoneOperationBackEndMap.PHONE_SETUP_RESEND,
    })
  }, [handleOperation])

  const handleVerify = useCallback(values => {
    handleOperation({
      operation: mfaConfig.SetPhoneOperationBackEndMap.PHONE_SETUP_VERIFY,
      ...values,
    })
  }, [handleOperation])

  return (
    <>
      { stepMode === mfaConfig.StepModeMap.SETUP_PHONE_STEP_ONE && (
        <VerifyPhoneStepOne
          defaultLocale={locale}
          phoneNumber={senderPhone}
          title="passport.phoneAuthentication.modal.title"
          rejectButtonText="passport.phoneAuthentication.modal.button.text.skip"
          handleReject={handleSkip}
          handleClose={handleSkip}
          handleConfirm={handleSend}
        />
      )}
      { stepMode === mfaConfig.StepModeMap.SETUP_PHONE_STEP_TWO && (
        <VerifyPhoneStepTwo
          handleClose={handleSkip}
          handleConfirm={handleVerify}
          handleResend={handleResend}
          handleReject={handleBack}
          phoneNumber={senderPhone}
          title="passport.phoneAuthentication.modal.title"
          okText="passport.phoneAuthentication.modal.button.text.setUp"
          countDownNumber={countDownNumber}
        />
      )}
    </>
  )
}

PhoneAuthenticationByStep.propTypes = {
  onSetUpPhone: func.isRequired,
  configureTrack: func,
  epid: string,
  stepMode: string.isRequired,
  locale: string,
  senderPhone: string,
  nextTimeWindow: string,
}
PhoneAuthenticationByStep.defaultProps = {
  configureTrack: noop,
  senderPhone: '',
  nextTimeWindow: '',
  epid: '',
  locale: 'en_US',
}

export default withI18n(PhoneAuthenticationByStep)
